import { useState, useEffect, useCallback, useMemo } from "react";
import { Modal, Spinner } from "react-bootstrap";
import { Document, Page, pdfjs } from "react-pdf";
import axios from "axios";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import storage from "../Assets/js/Global";
import { urls } from "../Url";

pdfjs.GlobalWorkerOptions.workerSrc = `${window.location.origin}/pdf.worker.min.js`;

export default function DocumentPreviewModal({ show, onClose, file }) {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [isPdfType, setIsPdfType] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const options = useMemo(
    () => ({
      cMapUrl: "cmaps/",
      cMapPacked: true,
    }),
    []
  );

  useEffect(() => {
    let objectUrl = null;
    const fetchPreviewUrl = async () => {
      if (!file?.id) return;
      setIsLoading(true);
      setPdfUrl(null);
      setError("");

      try {
        const loginObject = storage.get("loginObj") || {};
        const freshToken = storage.get("accessToken");
        const payload = {
          deviceID: loginObject.deviceID,
          role: 3,
          userKey: loginObject.userKey,
          companyKey: loginObject.companyKey,
          adminKey: "",
          document_id: file.id,
        };

        const res = await axios.post(urls.previewDocument, payload, {
          responseType: "blob",
          headers: {
            Authorization: `Bearer ${freshToken}`,
          },
        });

        const blob = new Blob([res.data], {
          type: res.headers["content-type"],
        });

        objectUrl = URL.createObjectURL(blob);
        setPdfUrl(objectUrl);
        setIsPdfType(Boolean(blob.type?.includes("pdf")));
      } catch (err) {
        console.error("Preview error:", err);
        setError("Failed to load preview");
      } finally {
        setIsLoading(false);
      }
    };

    if (show && file?.id) {
      fetchPreviewUrl();
    }

    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [show, file]);

  const onLoadSuccess = useCallback(({ numPages }) => {
    setNumPages(numPages);
  }, []);
  const truncateMiddle = (str, maxLength = 30) => {
    if (!str || str.length <= maxLength) return str;
    const start = str.slice(0, Math.ceil(maxLength / 2) - 3);
    const end = str.slice(-Math.floor(maxLength / 2));
    return `${start}...${end}`;
  };
  return (
    <Modal show={show} onHide={onClose} fullscreen centered>
      <Modal.Header closeButton className="py-2">
        <Modal.Title
          style={{
            overflow: "hidden",
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
          }}
          className="h5"
        >
          {truncateMiddle(file?.fileName || "Document Preview", 30)}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body
        className="d-flex flex-column align-items-center p-2"
        style={{
          touchAction: "none",
          WebkitUserSelect: "none",
          userSelect: "none",
        }}
      >
        {isLoading ? (
          <Spinner animation="border" />
        ) : error ? (
          <div className="text-danger">{error}</div>
        ) : !pdfUrl ? (
          <div>Loading Preview...</div>
        ) : isPdfType ? (
          <div
            className="d-flex flex-column align-items-center w-100"
            style={{
              maxWidth: "100vw",
              maxHeight: "100vh",
              width: "100%",
              overflow: "hidden",
            }}
          >
            <TransformWrapper
              minScale={0.5}
              maxScale={3}
              initialScale={0.6}
              doubleClick={{ mode: "zoomIn" }}
              pinch={{ disabled: false }}
              wheel={{ disabled: false }}
              alignmentAnimationTime={200}
              limitToBounds={true}
            >
              {({ zoomIn, zoomOut, resetTransform }) => (
                <>
                  <div
                    style={{
                      width: "100%",
                      flex: "1 1 auto",
                      overflow: "auto",
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <TransformComponent>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: 16,
                        }}
                      >
                        <Document
                          file={pdfUrl}
                          onLoadSuccess={onLoadSuccess}
                          loading={<div>Loading document...</div>}
                          error={
                            <div className="text-danger">
                              Failed to render PDF
                            </div>
                          }
                          options={options}
                        >
                          {Array.from(new Array(numPages || 0), (_, i) => (
                            <div
                              key={`page_wrapper_${i + 1}`}
                              style={{
                                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                                marginBottom: 12,
                                background: "#fff",
                                borderRadius: 4,
                              }}
                            >
                              <Page
                                key={`page_${i + 1}`}
                                pageNumber={i + 1}
                                scale={1} // controlled by wrapper
                                width={
                                  window.innerWidth > 768 ? 600 : undefined
                                }
                                renderTextLayer={false}
                                renderAnnotationLayer={false}
                                loading={
                                  <div style={{ padding: 20 }}>
                                    Loading page...
                                  </div>
                                }
                              />
                            </div>
                          ))}
                        </Document>
                      </div>
                    </TransformComponent>
                  </div>
                </>
              )}
            </TransformWrapper>
          </div>
        ) : (
          <img
            src={pdfUrl}
            alt="Preview"
            style={{ maxWidth: "100%", maxHeight: "80vh" }}
            onError={() => setPdfUrl(null)}
          />
        )}
      </Modal.Body>
      {/* <Modal.Footer className="justify-content-end p-2">
        {pdfUrl && (
          <a
            href={pdfUrl}
            download={file?.fileName || "document.pdf"}
            className="btn btn-primary"
          >
            Download
          </a>
        )}
      </Modal.Footer> */}
    </Modal>
  );
}
